import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
// Optional import for TouchID - requires react-native-touch-id package
let TouchID: any = null;
try {
  TouchID = require('react-native-touch-id');
} catch (error) {
  // TouchID not available - component will show error
}
import { useStripe } from '@stripe/stripe-react-native';

export interface TouchIDPaymentProps {
  amount: number;
  currency?: string;
  paymentMethodId?: string;
  clientSecret?: string;
  onSuccess?: (result: any) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  title?: string;
  subtitle?: string;
  style?: any;
}

const touchIDConfig = {
  title: 'Authentication Required',
  imageColor: '#e00606',
  sensorDescription: 'Touch sensor',
  sensorErrorDescription: 'Failed',
  cancelText: 'Cancel',
  fallbackLabel: 'Show Passcode',
  unifiedErrors: false,
  passcodeFallback: false,
};

export const TouchIDPayment: React.FC<TouchIDPaymentProps> = ({
  amount,
  currency = 'USD',
  paymentMethodId,
  clientSecret,
  onSuccess,
  onError,
  onCancel,
  title = 'Authorize Payment',
  subtitle,
  style,
}) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { confirmPayment } = useStripe();

  const checkBiometricSupport = async (): Promise<boolean> => {
    try {
      const biometryType = await TouchID.isSupported();
      return biometryType !== false;
    } catch (error) {
      return false;
    }
  };

  const authenticateWithBiometrics = async (): Promise<boolean> => {
    try {
      await TouchID.authenticate(
        'Authenticate to complete your payment',
        touchIDConfig
      );
      return true;
    } catch (error: any) {
      if (error.name === 'UserCancel' || error.name === 'UserFallback') {
        onCancel?.();
      } else {
        onError?.(`Authentication failed: ${error.message}`);
      }
      return false;
    }
  };

  const processPayment = async () => {
    if (!clientSecret) {
      onError?.('Payment configuration is missing');
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await confirmPayment(clientSecret, {
        paymentMethodType: 'Card',
      });

      if (error) {
        throw new Error(error.message);
      }

      onSuccess?.(paymentIntent);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Payment failed';
      onError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayment = async () => {
    setIsAuthenticating(true);

    try {
      // Check if biometric authentication is supported
      const isBiometricSupported = await checkBiometricSupport();

      if (!isBiometricSupported) {
        Alert.alert(
          'Biometric Authentication Not Available',
          'Your device does not support biometric authentication. Please use an alternative payment method.',
          [{ text: 'OK', onPress: onCancel }]
        );
        return;
      }

      // Authenticate with biometrics
      const isAuthenticated = await authenticateWithBiometrics();

      if (isAuthenticated) {
        await processPayment();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Authentication failed';
      onError?.(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const formatAmount = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getBiometricIcon = () => {
    if (Platform.OS === 'ios') {
      return '👆'; // Touch ID / Face ID icon
    }
    return '🔒'; // Generic biometric icon for Android
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.content}>
        <Text style={styles.title}>{title}</Text>

        {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

        <View style={styles.amountContainer}>
          <Text style={styles.amountLabel}>Amount</Text>
          <Text style={styles.amount}>{formatAmount(amount, currency)}</Text>
        </View>

        <TouchableOpacity
          style={[
            styles.payButton,
            (isAuthenticating || isProcessing) && styles.payButtonDisabled,
          ]}
          onPress={handlePayment}
          disabled={isAuthenticating || isProcessing}
        >
          <Text style={styles.biometricIcon}>{getBiometricIcon()}</Text>
          <Text style={styles.payButtonText}>
            {isAuthenticating
              ? 'Authenticating...'
              : isProcessing
                ? 'Processing...'
                : `Pay with ${Platform.OS === 'ios' ? 'Touch ID' : 'Biometrics'}`}
          </Text>
        </TouchableOpacity>

        <Text style={styles.securityNote}>
          Your payment information is secured with biometric authentication
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    margin: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  content: {
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 20,
    textAlign: 'center',
  },
  amountContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
    minWidth: 200,
    alignItems: 'center',
  },
  amountLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  amount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  payButton: {
    backgroundColor: '#3B82F6',
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 32,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  payButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  biometricIcon: {
    fontSize: 20,
    marginRight: 8,
  },
  payButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  securityNote: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 8,
  },
});

export default TouchIDPayment;
